import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import useAutoPaddingSync from '../hooks/use-auto-padding-sync';
import controlIds from '../constants/control-ids';

const {
  Context: ElementSettingsContext,
  components: { Toggle },
  helpers: { createBatch, combineBatches, convertPlainBatchToBreakpointBasedBatch, getBatchValues },
} = elementSettings;

const ENABLED_VALUE = '1px';
const DISABLED_VALUE = '0px';

function BorderControl({ elementIds, targets, onChange }) {
  const { resolvedState, deviceMode } = useContext(ElementSettingsContext);
  const { getAutoPaddingBatch } = useAutoPaddingSync();

  const isToggleOn = useMemo(() => {
    const values = getBatchValues(resolvedState, { elementIds, targets }).flat();

    return values.some((value) => value !== undefined && value !== DISABLED_VALUE);
  }, [resolvedState, elementIds, targets]);

  const handleChange = useCallback(
    (isActive) => {
      const value = isActive ? ENABLED_VALUE : DISABLED_VALUE;
      const breakpointBasedBatch = convertPlainBatchToBreakpointBasedBatch(
        createBatch({ elementIds, targets, value }),
        deviceMode.breakpointKey
      );

      onChange({
        batch: combineBatches([
          breakpointBasedBatch,
          getAutoPaddingBatch(breakpointBasedBatch, { elementIds }),
        ]),
        controlId: controlIds.BORDER,
        value: isActive,
      });
    },
    [deviceMode.breakpointKey, elementIds, getAutoPaddingBatch, onChange, targets]
  );

  return (
    <Toggle isToggleEnabled={isToggleOn} title={i18next.t('Border')} handleChange={handleChange} />
  );
}

BorderControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default BorderControl;
